import { FilterBar, Select, useTranslate } from '@athena/ui'

export interface PracticeFilterValue {
  subjectId?: string | undefined
}

export interface PracticeFiltersProps {
  value: PracticeFilterValue
  onChange: (next: PracticeFilterValue) => void
  subjects: ReadonlyArray<{ id: string; name: string }>
}

/**
 * "No filter" needs a value of its own: Radix refuses an empty string as an
 * item value, and an id could in principle be anything, so this is a word no
 * uuid can collide with. Same sentinel the dashboard filters use.
 */
const ANY = '__any__'

/**
 * The question a tutor asks of their shelf: what is it about.
 *
 * Nothing is filtered here. The chosen id goes into the URL and from there
 * into the query, so the server returns the narrowed list — a tutor with a
 * hundred practices should not download all of them so the browser can hide
 * most. It also means the filter is by id: matching on the displayed name
 * would break the moment a subject is read in another language.
 */
export function PracticeFilters({ value, onChange, subjects }: PracticeFiltersProps) {
  const t = useTranslate()
  const anyApplied = value.subjectId !== undefined

  return (
    <FilterBar
      clearLabel={t('common.clearFilters')}
      {...(anyApplied ? { onClear: () => onChange({}) } : {})}
    >
      <Select
        label={t('practice.filters.subject')}
        searchable
        visibleOptionCount={6}
        value={value.subjectId ?? ANY}
        onValueChange={(next) => onChange({ ...value, subjectId: next === ANY ? undefined : next })}
        options={[
          { value: ANY, label: t('practice.filters.everySubject') },
          ...subjects.map((subject) => ({ value: subject.id, label: subject.name })),
        ]}
      />
    </FilterBar>
  )
}
